import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { useCartSync } from "@/hooks/useCartSync";
import WhatsAppButton from "@/components/WhatsAppButton";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";

// Lazy-load all secondary routes
const ShopPage = lazy(() => import("./pages/ShopPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ManufacturersPage = lazy(() => import("./pages/ManufacturersPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ShippingPolicyPage = lazy(() => import("./pages/ShippingPolicyPage"));
const PaymentMethodsPage = lazy(() => import("./pages/PaymentMethodsPage"));
const WarrantyPolicyPage = lazy(() => import("./pages/WarrantyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ProductFamilyPage = lazy(() => import("./pages/ProductFamilyPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProductConditionsPage = lazy(() => import("./pages/ProductConditionsPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const ReturnPolicyPage = lazy(() => import("./pages/ReturnPolicyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const CartSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

const PageFallback = () => <div className="min-h-screen bg-background" />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <CartSyncProvider>
          <ScrollToTop />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/manufacturers" element={<ManufacturersPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
              <Route path="/payment-methods" element={<PaymentMethodsPage />} />
              <Route path="/warranty-policy" element={<WarrantyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/brand/:slug" element={<BrandPage />} />
              <Route path="/brand/:brandSlug/:familySlug" element={<ProductFamilyPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/product-conditions" element={<ProductConditionsPage />} />
              <Route path="/return-policy" element={<ReturnPolicyPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/my-account" element={<MyAccountPage />} />
              <Route path="/:slug" element={<SEOLandingPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </CartSyncProvider>
        </AuthProvider>
      </BrowserRouter>
      <WhatsAppButton />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
